import React, { useState, useMemo } from "react";
import { X, Trash2, Plus, Minus, Send, Loader2 } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { buildImageUrl, api } from "../lib/api";
import { reachGoal } from "../lib/metrika";
import { toast } from "sonner";

const DELIVERY_ZONES = [
  { id: "leninsky", label: "Ленинский район", price: 250 },
  { id: "zasviyazhsky", label: "Засвияжский район", price: 300 },
  { id: "railway", label: "Железнодорожный район", price: 300 },
  { id: "zavolzhsky", label: "Заволжский район", price: 350 },
  { id: "region", label: "Область", price: 250, perKm: 20 },
];

const MIN_ORDER = 1500;
const FREE_DELIVERY = 3000;
const BUSINESS_TIMEZONE_OFFSET_MS = 4 * 60 * 60 * 1000;

const toDateOnly = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
const getBusinessToday = () => {
  const businessNow = new Date(Date.now() + BUSINESS_TIMEZONE_OFFSET_MS);
  return new Date(
    businessNow.getUTCFullYear(),
    businessNow.getUTCMonth(),
    businessNow.getUTCDate(),
  );
};

const formatDateLabel = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${date.getFullYear()}`;
};

const getDeliveryDateLimits = () => {
  const today = getBusinessToday();
  return {
    min: addDays(today, 1),
    max: addMonths(today, 1),
  };
};

const parseDeliveryDate = (value) => {
  const match = value.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const parsed = new Date(year, month - 1, day);

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return toDateOnly(parsed);
};

const getDeliveryDateError = (value) => {
  const { min, max } = getDeliveryDateLimits();
  if (!value.trim()) return "Укажите дату доставки";

  const parsed = parseDeliveryDate(value);
  if (!parsed) return "Дата доставки — формат ДД.ММ.ГГГГ";

  if (parsed < min || parsed > max) {
    return `Дата доставки должна быть с ${formatDateLabel(min)} по ${formatDateLabel(max)}`;
  }

  return "";
};

const isCompletePhone = (value) => value.replace(/\D/g, "").length === 11;

const CartSidebar = () => {
  const {
    items,
    open,
    setOpen,
    updateQty,
    removeItem,
    itemsTotal,
    clear,
  } = useCart();

  const [zoneId, setZoneId] = useState("leninsky");
  const [distanceKm, setDistanceKm] = useState(0);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const deliveryDateHint = useMemo(() => {
    const { min, max } = getDeliveryDateLimits();
    return `с ${formatDateLabel(min)} по ${formatDateLabel(max)}`;
  }, []);

  const formatPhone = (raw) => {
    let digits = raw.replace(/\D/g, "");
    if (digits.startsWith("8")) digits = "7" + digits.slice(1);
    if (!digits.startsWith("7")) digits = "7" + digits;
    digits = digits.slice(0, 11);
    let result = "+7";
    if (digits.length > 1) result += " (" + digits.slice(1, 4);
    if (digits.length >= 4) result += ") " + digits.slice(4, 7);
    if (digits.length >= 7) result += "-" + digits.slice(7, 9);
    if (digits.length >= 9) result += "-" + digits.slice(9, 11);
    return result;
  };

  const clearFieldError = (field) => {
    setFieldErrors((current) => {
      if (!current[field]) return current;
      return { ...current, [field]: "" };
    });
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
    clearFieldError("phone");
  };
  const handlePhoneFocus = () => { if (!phone || phone === "+7") setPhone("+7 "); };
  const handlePhoneBlur = () => {
    if (!phone.trim()) return;
    if (!isCompletePhone(phone)) {
      const error = "Введите телефон полностью";
      setFieldErrors((current) => ({ ...current, phone: error }));
      toast.error(error);
    }
  };

  const formatDate = (raw) => {
    const d = raw.replace(/\D/g, "").slice(0, 8);
    if (d.length <= 2) return d;
    if (d.length <= 4) return d.slice(0, 2) + "." + d.slice(2);
    return d.slice(0, 2) + "." + d.slice(2, 4) + "." + d.slice(4);
  };

  const formatTime = (raw) => {
    const d = raw.replace(/\D/g, "").slice(0, 4);
    if (d.length <= 2) return d;
    return d.slice(0, 2) + ":" + d.slice(2);
  };

  const isValidTime = (v) => /^\d{2}:\d{2}$/.test(v);

  const handleDeliveryDateChange = (e) => {
    setDeliveryDate(formatDate(e.target.value));
    clearFieldError("deliveryDate");
  };

  const handleDeliveryDateBlur = () => {
    if (!deliveryDate.trim()) return;
    const error = getDeliveryDateError(deliveryDate);
    if (error) {
      setFieldErrors((current) => ({ ...current, deliveryDate: error }));
      toast.error(error);
    }
  };

  const zone = useMemo(
    () => DELIVERY_ZONES.find((z) => z.id === zoneId) || DELIVERY_ZONES[0],
    [zoneId],
  );

  const deliveryPrice = useMemo(() => {
    let base = zone.price;
    if (zone.perKm) base += (Number(distanceKm) || 0) * zone.perKm;
    if (zone.id !== "region" && itemsTotal >= FREE_DELIVERY) base = 0;
    return base;
  }, [zone, distanceKm, itemsTotal]);

  const total = itemsTotal + deliveryPrice;
  const belowMin = itemsTotal < MIN_ORDER;
  const missing = MIN_ORDER - itemsTotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (belowMin) {
      toast.error(`Минимальная сумма заказа — ${MIN_ORDER} ₽`);
      return;
    }

    const nextErrors = {};
    if (!isCompletePhone(phone)) {
      nextErrors.phone = "Укажите номер телефона";
    }

    const deliveryDateError = getDeliveryDateError(deliveryDate);
    if (deliveryDateError) {
      nextErrors.deliveryDate = deliveryDateError;
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors((current) => ({ ...current, ...nextErrors }));
      toast.error(nextErrors.phone || nextErrors.deliveryDate);
      return;
    }

    const spamEnabled = localStorage.getItem("pirogi_spam_protection") !== "false";
    const MAX_ORDERS_PER_HOUR = 3;
    const oneHourMs = 60 * 60 * 1000;
    let submitTimes = [];
    if (spamEnabled) {
      try {
        submitTimes = JSON.parse(localStorage.getItem("order_submit_times") || "[]");
      } catch {
        submitTimes = [];
      }
      submitTimes = submitTimes.filter((t) => Date.now() - t < oneHourMs);
      if (submitTimes.length >= MAX_ORDERS_PER_HOUR) {
        const minutesLeft = Math.ceil((oneHourMs - (Date.now() - submitTimes[0])) / 60000);
        toast.error(`Подождите ${minutesLeft} мин перед следующей заявкой`);
        return;
      }
    }

    if (deliveryTime && !isValidTime(deliveryTime)) {
      toast.error("Время доставки — формат ЧЧ:ММ");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        customer_name: name,
        phone,
        address,
        delivery_date: deliveryDate,
        delivery_time: deliveryTime,
        comment,
        delivery_zone: zone.label,
        delivery_price: deliveryPrice,
        items_total: itemsTotal,
        total,
        distance_km: zone.perKm ? Number(distanceKm) || 0 : 0,
        items: items.map((it) => ({
          product_id: it.product_id,
          name: it.name,
          price: it.price,
          quantity: it.quantity,
          unit: it.unit,
          subtotal: Math.round(it.price * it.quantity),
        })),
      };
      await api.post("/orders", payload);
      reachGoal("order_submit", {
        order_price: total,
        currency: "RUB",
        items_total: itemsTotal,
        delivery_price: deliveryPrice,
        delivery_zone: zone.label,
      });
      if (spamEnabled) {
        submitTimes.push(Date.now());
        localStorage.setItem("order_submit_times", JSON.stringify(submitTimes));
      }
      setSubmitted(true);
      clear();
      window.location.href = "tel:+79378764537";
    } catch (err) {
      toast.error(err?.message || "Не удалось отправить заявку. Попробуйте ещё раз.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    if (submitted) {
      // reset state shortly after close
      setTimeout(() => {
        setSubmitted(false);
        setName("");
        setPhone("");
        setAddress("");
        setDeliveryDate("");
        setDeliveryTime("");
        setComment("");
        setFieldErrors({});
      }, 400);
    }
  };

  return (
    <>
      {/* overlay */}
      <div
        onClick={close}
        className={`fixed inset-0 z-50 bg-[#2C2520]/40 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="cart-sidebar"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[460px] glass-panel transform transition-transform duration-400 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#E8E3DA]">
            <div>
              <div className="label-eyebrow">Ваша корзина</div>
              <div className="font-display text-2xl text-[#2C2520]">
                {items.length === 0 ? "Пусто" : `${items.length} позиций`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {items.length > 0 && (
                <button
                  onClick={clear}
                  title="Очистить корзину"
                  className="w-10 h-10 rounded-full bg-white border border-[#E8E3DA] flex items-center justify-center hover:border-[#9E3C3C] hover:text-[#9E3C3C] transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                onClick={close}
                data-testid="close-cart-button"
                className="w-10 h-10 rounded-full bg-white border border-[#E8E3DA] flex items-center justify-center hover:border-[#C65D3B] transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {submitted ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="order-success-screen">
              <div className="w-20 h-20 rounded-full bg-[#C65D3B] text-white flex items-center justify-center mb-6">
                <Send size={28} />
              </div>
              <h3 className="font-display text-3xl text-[#2C2520] mb-3">
                Спасибо за заявку!
              </h3>
              <p className="text-[#5C4F45] leading-relaxed mb-6">
                Заказ принят, скоро перезвоним и обсудим детали.
              </p>
              <button onClick={close} className="btn-primary" data-testid="close-success-button">
                Закрыть
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="empty-cart">
              <div className="font-display text-3xl text-[#2C2520] mb-3">
                Корзина пуста
              </div>
              <p className="text-[#5C4F45]">
                Выберите пироги в каталоге и они появятся здесь.
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                {items.map((it) => (
                  <div
                    key={it.key}
                    data-testid={`cart-item-${it.product_id}`}
                    className="flex gap-3 items-center"
                  >
                    <div className="w-16 h-16 rounded-2xl overflow-hidden bg-[#F2EBE1] flex-shrink-0">
                      <img
                        src={buildImageUrl(it.image_url)}
                        alt={it.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#2C2520] truncate">
                        {it.name}
                      </div>
                      <div className="text-xs text-[#5C4F45] mt-0.5">
                        {it.price} ₽ / {it.unit}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() =>
                            updateQty(
                              it.key,
                              it.unit === "кг"
                                ? Math.max(0, it.quantity - 1)
                                : it.quantity - 1,
                            )
                          }
                          className="w-7 h-7 rounded-full border border-[#E8E3DA] flex items-center justify-center hover:border-[#C65D3B] transition-colors"
                          data-testid={`cart-minus-${it.product_id}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm font-medium w-10 text-center">
                          {it.quantity} {it.unit}
                        </span>
                        <button
                          onClick={() => updateQty(it.key, it.quantity + 1)}
                          className="w-7 h-7 rounded-full border border-[#E8E3DA] flex items-center justify-center hover:border-[#C65D3B] transition-colors"
                          data-testid={`cart-plus-${it.product_id}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-[#2C2520]">
                        {Math.round(it.price * it.quantity)} ₽
                      </div>
                      <button
                        onClick={() => removeItem(it.key)}
                        className="text-xs text-[#5C4F45] hover:text-[#C65D3B] mt-1 inline-flex items-center gap-1"
                        data-testid={`cart-remove-${it.product_id}`}
                      >
                        <Trash2 size={12} /> убрать
                      </button>
                    </div>
                  </div>
                ))}

                <div className="pt-4 mt-4 border-t border-[#E8E3DA]">
                  <div className="label-eyebrow mb-3">Зона доставки</div>
                  <div className="space-y-2">
                    {DELIVERY_ZONES.map((z) => (
                      <label
                        key={z.id}
                        className={`flex items-center justify-between px-4 py-3 rounded-2xl border cursor-pointer transition-all ${
                          zoneId === z.id
                            ? "border-[#C65D3B] bg-white"
                            : "border-[#E8E3DA] bg-white/60 hover:border-[#C65D3B]/50"
                        }`}
                        data-testid={`zone-${z.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="zone"
                            checked={zoneId === z.id}
                            onChange={() => setZoneId(z.id)}
                            className="accent-[#C65D3B]"
                          />
                          <span className="text-sm text-[#2C2520]">
                            {z.label}
                          </span>
                        </div>
                        <span className="text-sm text-[#5C4F45]">
                          {z.price} ₽{z.perKm ? ` + ${z.perKm} ₽/км` : ""}
                        </span>
                      </label>
                    ))}
                  </div>
                  {zone.perKm && (
                    <div className="mt-3">
                      <label className="label-eyebrow block mb-2">
                        Километры от города
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={distanceKm}
                        onChange={(e) => setDistanceKm(e.target.value)}
                        className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B] text-[#2C2520]"
                        data-testid="distance-input"
                      />
                    </div>
                  )}
                </div>

                <div className="pt-4 mt-4 border-t border-[#E8E3DA] space-y-3">
                  <div className="label-eyebrow">Контактные данные</div>
                  <input
                    type="text"
                    placeholder="Ваше имя"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B] text-[#2C2520]"
                    data-testid="customer-name-input"
                  />
                  <div>
                    <label htmlFor="customer-phone" className="label-eyebrow block mb-2">
                      Телефон <span className="text-[#9E3C3C]">*</span>
                    </label>
                    <input
                      id="customer-phone"
                      type="tel"
                      placeholder="+7 (___) ___-__-__"
                      value={phone}
                      onChange={handlePhoneChange}
                      onFocus={handlePhoneFocus}
                      onBlur={handlePhoneBlur}
                      required
                      aria-invalid={Boolean(fieldErrors.phone)}
                      aria-describedby={fieldErrors.phone ? "customer-phone-error" : undefined}
                      className={`w-full px-4 py-3 rounded-2xl border bg-white focus:outline-none text-[#2C2520] ${
                        fieldErrors.phone
                          ? "border-[#9E3C3C] focus:border-[#9E3C3C]"
                          : "border-[#E8E3DA] focus:border-[#C65D3B]"
                      }`}
                      data-testid="customer-phone-input"
                    />
                    {fieldErrors.phone && (
                      <div id="customer-phone-error" className="text-xs text-[#9E3C3C] mt-1">
                        {fieldErrors.phone}
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Адрес доставки"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B] text-[#2C2520]"
                    data-testid="customer-address-input"
                  />
                  <div className="flex gap-3">
                    <div className="w-1/2">
                      <label htmlFor="delivery-date" className="label-eyebrow block mb-2">
                        Дата <span className="text-[#9E3C3C]">*</span>
                      </label>
                      <input
                        id="delivery-date"
                        type="text"
                        placeholder="ДД.ММ.ГГГГ"
                        value={deliveryDate}
                        onChange={handleDeliveryDateChange}
                        onBlur={handleDeliveryDateBlur}
                        className={`w-full px-4 py-3 rounded-2xl border bg-white focus:outline-none text-[#2C2520] ${
                          fieldErrors.deliveryDate
                            ? "border-[#9E3C3C] focus:border-[#9E3C3C]"
                            : "border-[#E8E3DA] focus:border-[#C65D3B]"
                        }`}
                        inputMode="numeric"
                        maxLength={10}
                        required
                        aria-invalid={Boolean(fieldErrors.deliveryDate)}
                        aria-describedby="delivery-date-help"
                      />
                      <div
                        id="delivery-date-help"
                        className={`text-xs mt-1 ${
                          fieldErrors.deliveryDate ? "text-[#9E3C3C]" : "text-[#8B7B6C]"
                        }`}
                      >
                        {fieldErrors.deliveryDate || deliveryDateHint}
                      </div>
                    </div>
                    <div className="w-1/2">
                      <label htmlFor="delivery-time" className="label-eyebrow block mb-2">
                        Время
                      </label>
                      <input
                        id="delivery-time"
                        type="text"
                        placeholder="ЧЧ:ММ"
                        value={deliveryTime}
                        onChange={(e) => setDeliveryTime(formatTime(e.target.value))}
                        className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B] text-[#2C2520]"
                        inputMode="numeric"
                        maxLength={5}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#E8E3DA] px-6 py-5 bg-white/70">
                <div className="flex justify-between text-sm text-[#5C4F45]">
                  <span>Товары</span>
                  <span>{itemsTotal} ₽</span>
                </div>
                <div className="flex justify-between text-sm text-[#5C4F45] mt-1">
                  <span>Доставка</span>
                  <span>{deliveryPrice} ₽</span>
                </div>
                {belowMin && (
                  <div className="text-xs text-[#9E3C3C] mt-2" data-testid="below-min-warning">
                    Не хватает {missing} ₽ до минимальной суммы заказа.
                  </div>
                )}
                {!belowMin && itemsTotal >= FREE_DELIVERY && zone.id !== "region" && (
                  <div className="text-xs text-[#4A5D4E] mt-2">
                    Бесплатная доставка по городу
                  </div>
                )}
                <div className="flex justify-between items-end mt-4 pt-4 border-t border-[#E8E3DA]">
                  <div>
                    <div className="label-eyebrow text-[10px]">Итого</div>
                    <div className="font-display text-4xl text-[#2C2520] leading-none">
                      {total}
                      <span className="font-body font-normal text-xl ml-1 text-[#5C4F45] align-baseline">₽</span>
                    </div>
                  </div>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || belowMin}
                    className="btn-primary"
                    data-testid="submit-order-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Отправка...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Позвонить менеджеру
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default CartSidebar;
